import React, { useState, useEffect } from "react";
import "antd/dist/reset.css";
import { Table, Select, Button, Pagination, Modal, Checkbox, message, Tooltip } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);
const { Option } = Select;

const API_DOMAIN = process.env.REACT_APP_TPOC_API_DOMAIN;

const MilestoneEventsPage = ({ loanId }) => {

    const [allData, setAllData] = useState([]); // Stores ALL records from API
    const [displayData, setDisplayData] = useState([]); // Current page data
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });

    const [pageSize, setPageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [inputPropertyValue, setInputPropertyValue] = useState('');
    const [inputValue, setInputValue] = useState('');
    const [error, setError] = useState(null);
    const [resourceType, setResourceType] = useState('LoanNumber');
   
    const columns = [
        { title: "Event", dataIndex: "snapshotSource", key: "snapshotSource" },
        { title: "User Name", dataIndex: "userId", key: "userId" },
        { title: "Completed Date", dataIndex: "createdDate", key: "createdDate" },
    ];

  
    // final columns (Select first)
    const finalColumns = [
       ...columns.map(col => {
    if (col.dataIndex === "createdDate") {
        return {
            ...col,
            render: (text) =>
            text
                ? `${dayjs.tz(text, "America/Los_Angeles").format("YYYY-MM-DD HH:mm:ss")} PT`
                : "N/A"
        };
    }
    return col;
})
    ];

    // Fetch list / filtering (unchanged except keeping existing logic)
    const fetchFilteredData = async (page = 1, size = pageSize) => {
        if (isLoading) return;

        setIsLoading(true);
        setError(null);

        const payload = {
            filters: {}, // Regular filters go here
            sortBy: "CreatedDate",
            sortDescending: true,
            propertyName: inputValue,
            propertyValue: inputPropertyValue
        };

        if (resourceType === "ResourceID") {
            payload.filters['resourceID'] = loanId;
        } else if (resourceType === "LoanNumber") {
            payload.filters['loanNumber'] = loanId;
        }

        try {
            const response = await fetch(`${API_DOMAIN}/api/audit/loansnapshot/filter`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP ${response.status}: ${errorText}`);
            }

            const result = await response.json();
            const items = result.data?.items || [];
     
            setAllData(items);
            setPagination(prev => ({
                ...prev,
                current: 1,
                total: items.length
            }));
            setTotalItems(result.data?.totalCount || result.totalCount || items.length);
           
            setDisplayData(items.slice(0, pageSize));
        } catch (err) {
            console.error("Error fetching filtered data:", err);
            setError(err?.message || "Failed to fetch");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (loanId) {
            fetchFilteredData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loanId]);


    // Helper function to update displayed data
    const updateDisplayData = (fullData, current, pageSize) => {
        const startIndex = (current - 1) * pageSize;
        setDisplayData(fullData.slice(startIndex, startIndex + pageSize));
        setPagination(prev => ({ ...prev, current, pageSize }));
    };

    const handlePageChanges = (page) => {
        updateDisplayData(allData, page, pagination.pageSize);
    };

    const handlePageSizeChanges = (size) => {
        updateDisplayData(allData, 1, size); // Reset to first page
    };


    return (
        <div className="space-y-6">
            <div className="w-full p-4">
                <div className="min-h-screen">
                    <div className="overflow-hidden p-2">
                        {error && (
                            <div className="mb-4 p-3 rounded bg-red-100 border border-red-400 text-red-700">
                                <strong>Error:</strong> {error}
                            </div>
                        )}
                        <div className="flex items-center gap-2 mt-2 md:mt-0 mb-4">
                            <span className="text-sm text-gray-600">Total Records: <strong>{totalItems}</strong></span>
                        </div>
                        {/* Table View */}
                        <div className="md:block overflow-x-auto">
                            <Table
                                columns={finalColumns}
                                dataSource={displayData} // Use displayData instead of data
                                rowKey="id"
                                pagination={false}
                                className="shadow-md"
                                loading={isLoading}
                            />
                        </div>
                        {/* Pagination Controls */}
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-gray-100 p-4 rounded-md mb-4">
                            <div className="flex items-center sm:flex-1">
                                <span className="mr-2 text-gray-700">Rows per page:</span>
                                <Select
                                    value={pageSize}
                                    onChange={handlePageSizeChanges}
                                    className="w-20"
                                    disabled={isLoading}
                                >
                                    {[3, 5, 10, 20, 50].map((size) => (
                                        <Option key={size} value={size}>{size}</Option>
                                    ))}
                                </Select>
                            </div>
                            <div className="w-full sm:w-auto sm:flex sm:justify-end mt-4 sm:mt-0">
                                <div className="pagination-container flex items-center space-x-2">
                                    <Pagination
                                        current={pagination.current}
                                        total={pagination.total}
                                        pageSize={pagination.pageSize}
                                        onChange={handlePageChanges}
                                        showSizeChanger={false}
                                        className="text-center sm:text-right"
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
export default MilestoneEventsPage;